/**
 * Auth state for the SPA, backed by an httpOnly session cookie.
 *
 * The session cookie (set by /api/auth/login + /signup) is httpOnly, so
 * JS can't read it — there's no token in storage to decode. Auth state is
 * therefore sourced entirely from GET /api/me:
 *   • on mount we call /api/me; 200+authed → signed in (with
 *     tier / isAdmin / userId), 401 or error → signed out.
 *   • after a login/signup (which already set the cookie) we re-fetch
 *     /api/me to populate state.
 *   • logout POSTs /api/auth/logout (clears the cookie) then resets state.
 *
 * Cross-tab note: cookies are shared across tabs automatically, but there
 * is no storage event for cookie changes — so a logout in one tab reflects
 * in another only on its next /api/me (navigation / refresh). Acceptable
 * for now; a BroadcastChannel ping is a possible follow-up.
 */

import { useCallback, useEffect, useState } from 'react';
import { AuthState, MeResponse, Tier } from '../lib/types';
import { apiGet, apiPost, clearStoredAuth, encodeCreds } from '../lib/api';

interface UseAuthState extends AuthState {
  loading: boolean;
}

const SIGNED_OUT: UseAuthState = {
  authed: false,
  username: null,
  isAdmin: false,
  tier: 'free',
  userId: null,
  loading: false,
};

function meToState(j: MeResponse): UseAuthState {
  return {
    authed: !!j.authed,
    username: j.username ?? null,
    isAdmin: !!j.is_admin,
    tier: (j.tier ?? 'free') as Tier,
    userId: typeof j.id === 'number' ? j.id : null,
    loading: false,
  };
}

export function useAuth() {
  // Unknown until /api/me resolves (we can't read the httpOnly cookie).
  const [state, setState] = useState<UseAuthState>({ ...SIGNED_OUT, loading: true });

  const refresh = useCallback(async () => {
    try {
      const j = await apiGet<MeResponse>('/me');
      setState(j.authed ? meToState(j) : { ...SIGNED_OUT });
    } catch {
      setState({ ...SIGNED_OUT });
    }
  }, []);

  // Resolve auth on mount from /api/me (the cookie rides along on the
  // credentialed request).
  useEffect(() => {
    let cancelled = false;
    apiGet<MeResponse>('/me')
      .then(j => { if (!cancelled) setState(j.authed ? meToState(j) : { ...SIGNED_OUT }); })
      .catch(() => { if (!cancelled) setState({ ...SIGNED_OUT }); });
    return () => { cancelled = true; };
  }, []);

  /** Call after a successful /api/auth/login or /signup. The backend has
   *  already set the session cookie in that response, so we just refresh
   *  state from /api/me. The token argument is ignored — retained for
   *  call-site compatibility with the previous Bearer flow. */
  const loginWithJwt = useCallback((_token?: string) => {
    setState(prev => ({ ...prev, loading: true }));
    void refresh();
  }, [refresh]);

  const logout = useCallback(() => {
    // Clear the cookie server-side; reset local state immediately so the
    // UI flips to signed-out without waiting on the round-trip.
    void apiPost('/auth/logout', {}).catch(() => { /* best-effort */ });
    clearStoredAuth();
    setState({ ...SIGNED_OUT });
  }, []);

  return { ...state, loginWithJwt, refresh, logout, encodeCreds };
}
